import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Logo from "Assets/Common/logo.png";

import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Loading from "Components/Global/Loading";

import * as S from "./styles";

/**
 * Página PÚBLICA da apresentação da seletiva.
 *
 * O link chega pelo e-mail que o admin dispara ao patrocinador. Não há login: a
 * autorização é o token opaco assinado em `?t=`, válido por 3 meses. A URL do
 * arquivo no S3 é gerada a cada acesso (vale 1h), e o backend confere o estado
 * atual da seletiva — despublicar ou inativar derruba o link antes do prazo.
 */
const SelectivePresentation = () => {
  const { api } = UseServices();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("t");

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  // O React 18 em dev monta duas vezes; sem o ref o arquivo seria solicitado
  // (e o download disparado) em duplicidade.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;

    requested.current = true;

    if (!token) {
      setLoading(false);
      setResult({
        variant: "error",
        title: "Link inválido",
        message:
          "Este endereço está incompleto. Abra o link diretamente do e-mail que você recebeu.",
      });

      return;
    }

    const fetchPresentation = async () => {
      try {
        const { data } = await api.post(
          "/select-projects/get-presentation-by-token",
          { token },
        );

        setResult({
          variant: "success",
          title: data?.file_name || "Apresentação disponível",
          message: "O download começa automaticamente.",
          select_project_name: data?.select_project_name,
          signed_url: data?.signed_url,
          file_name: data?.file_name,
        });

        // Dispara o download; o botão abaixo fica como alternativa, porque
        // alguns navegadores bloqueiam o redirecionamento automático.
        if (data?.signed_url) window.location.assign(data.signed_url);
      } catch (error) {
        // A mensagem do backend já é amigável (link inválido/expirado,
        // apresentação indisponível) — aqui ela É o conteúdo da tela.
        setResult({
          variant: "error",
          title: "Não foi possível abrir a apresentação",
          message:
            error?.response?.data?.error ||
            "O link pode ter expirado. Solicite uma apresentação atualizada.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPresentation();
    // eslint-disable-next-line
  }, [token]);

  return (
    <S.Wrapper>
      <S.Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="selective-presentation-title"
      >
        <S.Logo src={Logo} alt="Sevenx" />

        {loading ? (
          <>
            <Loading />
            <S.Message>Preparando a apresentação...</S.Message>
          </>
        ) : (
          <>
            <S.Icon $variant={result?.variant}>
              {result?.variant === "error" ? "!" : "✓"}
            </S.Icon>

            <S.Title id="selective-presentation-title">{result?.title}</S.Title>

            <S.Message>
              {result?.message}
              {result?.select_project_name ? (
                <>
                  <br />
                  Seletiva:{" "}
                  <S.SelectiveName>
                    {result.select_project_name}
                  </S.SelectiveName>
                </>
              ) : null}
            </S.Message>

            {result?.signed_url ? (
              <S.Actions>
                <Button
                  type="button"
                  text={`Baixar ${result.file_name || "arquivo"}`}
                  onClick={() => window.location.assign(result.signed_url)}
                />
              </S.Actions>
            ) : null}
          </>
        )}
      </S.Modal>
    </S.Wrapper>
  );
};

export default SelectivePresentation;
